import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { agent } from 'services/service';
import { AttendanceCalendarResponse } from 'services/AttendanceTrackingService';
import { STATUS_LABELS } from './calendarShared';
import { getCalendarDefaults, mapCalendarDaysToRecords } from './calendarApi';

export const useMyAttendanceCalendar = (rangeStart: Date, rangeEnd: Date) => {
  const [data, setData] = useState<AttendanceCalendarResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const fromKey = format(rangeStart, 'yyyy-MM-dd');
  const toKey = format(rangeEnd, 'yyyy-MM-dd');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const isInitialLoad = !hasLoadedRef.current;
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      try {
        const response =
          await agent.AttendanceTrackingService.getMyAttendanceCalendar(
            fromKey,
            toKey
          );

        if (!cancelled) {
          setData(response.data);
          hasLoadedRef.current = true;
        }
      } catch {
        if (!cancelled) {
          if (isInitialLoad) {
            setData(null);
          }
          setError('Failed to load attendance calendar.');
        }
      } finally {
        if (!cancelled) {
          if (isInitialLoad) {
            setLoading(false);
          }
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [fromKey, toKey]);

  return useMemo(() => {
    const defaults = data ? getCalendarDefaults(data) : null;

    return {
      epfNumber: data?.epfNumber ?? '',
      recordsByDate: data
        ? mapCalendarDaysToRecords(data.days)
        : new Map<string, import('./calendarShared').DayRecord>(),
      employeeName: defaults?.employeeName ?? '',
      minimumOfficeDays: defaults?.minimumOfficeDays ?? 8,
      legendItems: defaults?.legendItems ?? [],
      statusLabels: defaults?.statusLabels ?? STATUS_LABELS,
      loading,
      error
    };
  }, [data, loading, error]);
};
